import React, { FC, useState, useMemo, useCallback, Ref } from 'react';

import { StepIndex, VerticalStep } from '@code/innovance-react-ui-kit';
import { useTranslation } from 'react-i18next';
import StepperView, { StepperRefProps, StepView } from './StepperView';
import Stepper, { getProgressConfig } from './Stepper';

interface ApplicationProps {
  stepperRef: Ref<StepperRefProps>;
  steps: StepView[];
  stepsVertical: {
    subSteps?: {
      text: string;
      index: number;
    }[];
    text: string;
    index: number;
  }[];
  isCompleted?: boolean;
}

const Application: FC<ApplicationProps> = ({ stepperRef, steps, stepsVertical, isCompleted }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<StepIndex>({ main: 1, sub: 1 });

  const stepMapping: StepIndex[] = useMemo(() => {
    const emptyList: StepIndex[] = [];
    return stepsVertical.reduce(
      (prevSteps, current) => [
        ...prevSteps,
        ...(current.subSteps
          ? current.subSteps.map((item) => ({ main: current.index, sub: item.index }))
          : [{ main: current.index, sub: 1 }]),
      ],
      emptyList
    );
  }, [stepsVertical]);

  const onStepChange = (index: number): void => {
    setCurrentStep(stepMapping[index]);
    setCurrentIndex(index);
  };

  const handleChangeStep = useCallback(
    (main: VerticalStep, sub?: VerticalStep): void => {
      const index: number = stepMapping.findIndex(
        (step) => step.main === main.index && step.sub === (sub?.index || 1)
      );
      if (index !== -1) {
        setCurrentIndex(index);
        if (typeof stepperRef === 'function') {
          stepperRef(null);
        } else if (stepperRef) {
          stepperRef.current?.goToStep(index);
        }
      }
    },
    [stepMapping, stepperRef]
  );
  return (
    <>
      <StepperView onChange={onStepChange} ref={stepperRef} steps={steps} />
      <Stepper
        currentStep={currentStep}
        handleChangeStep={handleChangeStep}
        progressConfig={getProgressConfig(currentIndex, steps.length, 100, t, isCompleted)}
        stepConfig={stepsVertical}
      />
    </>
  );
};

export default Application;
